/**
 * Configuração Visual da Transição Circular
 *
 * Sistema parametrizável para controlar quais efeitos mostram círculos de transição
 * e suas cores/estilos personalizados.
 *
 * @module transitionVisualConfig
 */

import configData from './transitionVisualConfig.json';

/**
 * Obter lista de IDs de efeitos que suportam transição
 *
 * @returns {string[]} Array com IDs dos efeitos habilitados
 *
 * @example
 * const effectIds = getEffectsWithTransition();
 * // ['zoomBlur', 'gaussianBlur', 'ditherBlur', ...]
 */
export function getEffectsWithTransition() {
  return configData.effectsWithTransition
    .filter(effect => effect.enabled)
    .map(effect => effect.id);
}

/**
 * Verificar se um efeito específico suporta transição
 *
 * @param {string} effectId - ID do efeito
 * @returns {boolean} true se o efeito suporta transição
 *
 * @example
 * if (hasTransitionSupport('gaussianBlur')) {
 *   // Mostrar círculos de transição
 * }
 */
export function hasTransitionSupport(effectId) {
  return configData.effectsWithTransition.some(
    effect => effect.id === effectId && effect.enabled
  );
}

/**
 * Obter cores personalizadas para um efeito específico
 *
 * @param {string} effectId - ID do efeito
 * @returns {Object} Objeto com cores personalizadas ou cores padrão
 *
 * @example
 * const colors = getEffectColors('ditherBlur');
 * // { outerCircle: '#3b82f6', innerCircle: '#60a5fa', ... }
 */
export function getEffectColors(effectId) {
  const effect = configData.effectsWithTransition.find(e => e.id === effectId);

  if (!effect || !effect.enabled) {
    return configData.defaultColors;
  }

  // Mesclar cores do efeito com cores padrão (fallback)
  return {
    ...configData.defaultColors,
    ...effect.colors
  };
}

/**
 * Obter configurações visuais (tamanhos, espessuras, etc)
 *
 * @returns {Object} Configurações visuais
 *
 * @example
 * const settings = getVisualSettings();
 * // { outerLineWidth: 2, centerPointSize: 12, ... }
 */
export function getVisualSettings() {
  return configData.visualSettings;
}

/**
 * Obter configuração completa de um efeito
 *
 * @param {string} effectId - ID do efeito
 * @returns {Object|null} Configuração completa ou null se não encontrado
 *
 * @example
 * const config = getEffectConfig('zoomBlur');
 * // { id: 'zoomBlur', name: 'Zoom Blur', enabled: true, colors: {...} }
 */
export function getEffectConfig(effectId) {
  return configData.effectsWithTransition.find(e => e.id === effectId) || null;
}

/**
 * Obter nome legível de um efeito
 *
 * @param {string} effectId - ID do efeito
 * @returns {string} Nome do efeito ou ID se não encontrado
 *
 * @example
 * const name = getEffectName('gaussianBlur');
 * // "Gaussian Blur"
 */
export function getEffectName(effectId) {
  const effect = configData.effectsWithTransition.find(e => e.id === effectId);
  return effect ? effect.name : effectId;
}

/**
 * Exportação default com todas as funções
 */
export default {
  getEffectsWithTransition,
  hasTransitionSupport,
  getEffectColors,
  getVisualSettings,
  getEffectConfig,
  getEffectName
};
